<div class="bai-page-shell bai-documents-root" aria-label="Client documents">

  @if (loading()) {
    <!-- Skeleton loading state -->
    <div class="bai-surface-card bai-docs-frame bai-docs-skeleton" aria-busy="true" aria-label="Loading documents">
      <div class="bai-docs-header-skeleton">
        <div>
          <p-skeleton width="160px" height="28px" />
          <p-skeleton width="240px" height="16px" styleClass="mt-2" />
        </div>
        <p-skeleton width="120px" height="36px" borderRadius="6px" />
      </div>
      <div class="bai-docs-filters-skeleton">
        <p-skeleton width="180px" height="36px" borderRadius="6px" />
        <p-skeleton width="140px" height="36px" borderRadius="6px" />
        <p-skeleton width="220px" height="36px" borderRadius="6px" />
      </div>
      @for (_ of [1,2,3,4,5]; track $index) {
        <p-skeleton width="100%" height="48px" styleClass="mb-1" />
      }
    </div>
  } @else if (!state()) {
    <!-- Empty state: no session selected -->
    <div class="bai-surface-card bai-docs-frame bai-docs-empty" role="status" aria-label="No client selected">
      <i class="pi pi-folder bai-docs-empty-icon" aria-hidden="true"></i>
      <p class="bai-docs-empty-title">No client selected</p>
      <p class="bai-docs-empty-sub">Select a client to view their documents.</p>
    </div>
  } @else {
    @let s = state()!;

    <div class="bai-surface-card bai-docs-frame">
      <!-- Header -->
      <div class="bai-section-header bai-docs-header">
        <div>
          <h2 class="bai-section-title bai-docs-title">Documents</h2>
          <p class="bai-section-subtitle bai-docs-subtitle">Client documents, reports, and uploaded files</p>
        </div>
        <p-button
          label="Upload File"
          icon="pi pi-upload"
          [disabled]="true"
          pTooltip="Upload coming soon"
          tooltipPosition="left"
          aria-label="Upload file (coming soon)"
        />
      </div>

      <!-- Filter bar -->
      <div class="bai-docs-filters" role="toolbar" aria-label="Document filters">
        <!-- Search -->
        <div class="bai-docs-search-wrap">
          <i class="pi pi-search bai-docs-search-icon" aria-hidden="true"></i>
          <input
            class="bai-docs-search"
            type="search"
            placeholder="Search documents…"
            [ngModel]="searchQuery()"
            (ngModelChange)="searchQuery.set($event)"
            aria-label="Search documents"
          />
        </div>

        <!-- Category filter -->
        <select
          class="bai-docs-select"
          [ngModel]="selectedCategory()"
          (ngModelChange)="selectedCategory.set($event)"
          aria-label="Filter by category"
        >
          @for (cat of categories; track cat) {
            <option [value]="cat">{{ categoryLabel(cat) }}</option>
          }
        </select>

        <!-- File type filter -->
        <select
          class="bai-docs-select"
          [ngModel]="selectedFileType()"
          (ngModelChange)="selectedFileType.set($event)"
          aria-label="Filter by file type"
        >
          <option value="all">All Types</option>
          @for (ft of uniqueFileTypes(); track ft) {
            <option [value]="ft">{{ ft.toUpperCase() }}</option>
          }
        </select>

        <!-- Sort -->
        <select
          class="bai-docs-select"
          [ngModel]="sortField()"
          (ngModelChange)="sortField.set($event)"
          aria-label="Sort by field"
        >
          @for (sf of sortFields; track sf.value) {
            <option [value]="sf.value">{{ sf.label }}</option>
          }
        </select>

        <!-- Sort direction toggle -->
        <button
          class="bai-docs-sort-dir-btn"
          type="button"
          (click)="sortDir.set(sortDir() === 'desc' ? 'asc' : 'desc')"
          [attr.aria-label]="sortDir() === 'desc' ? 'Sorted descending, click for ascending' : 'Sorted ascending, click for descending'"
          [attr.aria-pressed]="sortDir() === 'asc'"
          pTooltip="{{ sortDir() === 'desc' ? 'Newest / Largest first' : 'Oldest / Smallest first' }}"
          tooltipPosition="top"
        >
          <i [class]="sortDir() === 'desc' ? 'pi pi-sort-amount-down' : 'pi pi-sort-amount-up'"></i>
        </button>
      </div>

      <!-- Document count -->
      <div class="bai-docs-count" aria-live="polite" aria-atomic="true">
        @if (filteredDocuments().length === s.documents.length) {
          <span>{{ s.documents.length }} documents</span>
        } @else {
          <span>{{ filteredDocuments().length }} of {{ s.documents.length }} documents</span>
        }
      </div>

      <!-- Document table -->
      @if (filteredDocuments().length === 0) {
        <div class="bai-docs-empty bai-docs-empty--inline" role="status" aria-label="No matching documents">
          <i class="pi pi-filter-slash bai-docs-empty-icon" aria-hidden="true"></i>
          <p class="bai-docs-empty-title">No documents match your filters</p>
          <p class="bai-docs-empty-sub">Try adjusting your search or filter criteria.</p>
        </div>
      } @else {
        <div class="bai-docs-table-wrap">
          <table class="bai-data-table bai-docs-table" role="table" aria-label="Document list">
            <thead>
              <tr>
                <th baiColResize scope="col" class="bai-docs-th bai-docs-th--icon" data-col-min-width="48" aria-label="File type icon"></th>
                <th baiColResize scope="col" class="bai-docs-th bai-docs-th--sortable" [attr.aria-sort]="sortField() === 'fileName' ? (sortDir() === 'asc' ? 'ascending' : 'descending') : 'none'">
                  <button type="button" class="bai-docs-th-sort-btn" (click)="toggleSort('fileName')" aria-label="Sort by file name">
                    File Name
                    <i [class]="sortIcon('fileName')" aria-hidden="true"></i>
                  </button>
                </th>
                <th baiColResize scope="col" class="bai-docs-th bai-docs-th--sortable" [attr.aria-sort]="sortField() === 'category' ? (sortDir() === 'asc' ? 'ascending' : 'descending') : 'none'">
                  <button type="button" class="bai-docs-th-sort-btn" (click)="toggleSort('category')" aria-label="Sort by category">
                    Category
                    <i [class]="sortIcon('category')" aria-hidden="true"></i>
                  </button>
                </th>
                <th baiColResize scope="col" class="bai-docs-th bai-docs-th--sortable bai-cell-nowrap" [attr.aria-sort]="sortField() === 'uploadedAt' ? (sortDir() === 'asc' ? 'ascending' : 'descending') : 'none'">
                  <button type="button" class="bai-docs-th-sort-btn" (click)="toggleSort('uploadedAt')" aria-label="Sort by upload date">
                    Upload Date
                    <i [class]="sortIcon('uploadedAt')" aria-hidden="true"></i>
                  </button>
                </th>
                <th baiColResize scope="col" class="bai-docs-th bai-cell-nowrap">Uploaded By</th>
                <th baiColResize scope="col" class="bai-docs-th bai-cell-nowrap">Type</th>
                <th baiColResize scope="col" class="bai-docs-th bai-docs-th--sortable bai-cell-nowrap" [attr.aria-sort]="sortField() === 'fileSize' ? (sortDir() === 'asc' ? 'ascending' : 'descending') : 'none'">
                  <button type="button" class="bai-docs-th-sort-btn" (click)="toggleSort('fileSize')" aria-label="Sort by file size">
                    Size
                    <i [class]="sortIcon('fileSize')" aria-hidden="true"></i>
                  </button>
                </th>
                <th baiColResize scope="col" class="bai-docs-th bai-docs-th--actions bai-cell-nowrap" data-col-min-width="96">Actions</th>
              </tr>
            </thead>
            <tbody>
              @for (doc of filteredDocuments(); track trackDocument($index, doc)) {
                <tr class="bai-docs-row">
                  <td class="bai-docs-td bai-docs-td--icon">
                    <i [class]="'pi ' + getFileIcon(doc.fileType)" class="bai-docs-file-icon" [attr.aria-label]="doc.fileType + ' file'"></i>
                  </td>
                  <td class="bai-docs-td bai-docs-td--name">
                    <span class="bai-docs-file-name">{{ doc.fileName }}</span>
                  </td>
                  <td class="bai-docs-td">
                    <p-tag
                      [value]="getCategoryLabel(doc.category)"
                      [severity]="getCategorySeverity(doc.category)"
                    />
                  </td>
                  <td class="bai-docs-td bai-docs-td--date bai-cell-nowrap">{{ formatDate(doc.uploadedAt) }}</td>
                  <td class="bai-docs-td bai-docs-td--uploader bai-cell-nowrap">{{ doc.uploadedBy }}</td>
                  <td class="bai-docs-td bai-cell-nowrap">
                    <span class="bai-docs-type-badge">{{ doc.fileType.toUpperCase() }}</span>
                  </td>
                  <td class="bai-docs-td bai-docs-td--size bai-cell-nowrap">{{ formatFileSize(doc.fileSize) }}</td>
                  <td class="bai-docs-td bai-docs-td--actions bai-cell-nowrap">
                    <p-button
                      icon="pi pi-download"
                      [text]="true"
                      [rounded]="true"
                      severity="secondary"
                      pTooltip="Download (coming soon)"
                      tooltipPosition="top"
                      [disabled]="true"
                      [attr.aria-label]="'Download ' + doc.fileName"
                    />
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      }
    </div>
  }
</div>
